// Send a proof to the client (/proofs/:id/send) — Stephanie 2026-10-12.
//
// The rep lands here from the "Proof sent" alert or email. They look over the
// proof, tick one, some or all of the client's contacts (the order's main
// contact starts ticked), pick a saved template or start blank, and send. Each
// contact gets their own email from the rep with the proof attached and a
// "Review your proof" button (Approve / Request changes). Words in {braces}
// are filled in per person. The client never sees the PO #.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { ProofPreview } from "@/components/proofs/ProofPreview";

interface SendInfo {
  proof: { id: string; status: string; version: number; file: { url: string | null; name: string; mimeType: string } | null };
  signature?: { name: string; signedAt: string; termsVersion: string; source: "email_link" | "client_portal" } | null;
  poId: string;
  poNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  item: string;
  repName: string;
  contacts: Array<{ id: string; name: string; firstName: string; email: string | null; main: boolean }>;
  /** True once order contacts are on: only the order's contacts can be sent to (required). */
  orderContactsRequired?: boolean;
  accountContacts?: Array<{ id: string; name: string; email: string | null }>;
  templates: Array<{ id: string; name: string; subject: string; body: string }>;
}

const STATUS_NOTE: Record<string, string> = {
  awaiting_client: "Already sent to the client — sending again emails the people you tick.",
  client_approved: "The client approved this proof.",
  rejected: "The client asked for changes on this proof. Upload a revised proof from the Production Report.",
  revised: "A newer version of this proof was uploaded — send that one instead.",
};

export function ProofSendPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["proofs", id, "send-info"], queryFn: () => api<SendInfo>(`/proofs/${id}/send-info`), enabled: !!id });
  const info = q.data;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ sent: number; failed: string[] } | null>(null);
  const [adding, setAdding] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const addToOrder = useMutation({
    mutationFn: () =>
      api(`/projects/${info!.projectId}/order-contacts/add`, { method: "POST", body: JSON.stringify({ contact_ids: [...adding] }) }),
    onSuccess: () => {
      setAdding(new Set());
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ["proofs", id, "send-info"] });
      void qc.invalidateQueries({ queryKey: ["projects", info!.projectId, "order-contacts"] });
    },
  });

  // Start with the order's main contact ticked.
  useEffect(() => {
    if (info) setPicked(new Set(info.contacts.filter((c) => c.main && c.email).map((c) => c.id)));
  }, [info]);

  const canSend = info && ["awaiting_rep", "awaiting_client"].includes(info.proof.status);
  const withEmail = useMemo(() => info?.contacts.filter((c) => c.email) ?? [], [info]);
  const allTicked = withEmail.length > 0 && withEmail.every((c) => picked.has(c.id));

  const send = useMutation({
    mutationFn: () =>
      api<{ sent: number; failed: string[] }>(`/proofs/${id}/send-to-client`, {
        method: "POST",
        body: JSON.stringify({ contact_ids: [...picked], subject, body }),
      }),
    onSuccess: (r) => {
      setDone(r);
      void qc.invalidateQueries({ queryKey: ["proofs", id, "send-info"] });
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(b?.error ?? "We couldn't send it. Try again.");
    },
  });

  if (q.isLoading) return <div className="mx-auto w-full space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-6 animate-pulse rounded bg-canvas" />)}</div>;
  if (q.isError || !info) return <p className="mx-auto w-full text-risk" role="alert">We couldn't load this proof. Refresh to try again.</p>;

  const pickTemplate = (tid: string) => {
    setTemplateId(tid);
    const t = info.templates.find((x) => x.id === tid);
    setSubject(t?.subject ?? "");
    setBody(t?.body ?? "");
  };
  const toggle = (cid: string) => {
    const next = new Set(picked);
    if (next.has(cid)) next.delete(cid);
    else next.add(cid);
    setPicked(next);
  };

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow={`Proofs / PO #${info.poNumber}`}
        title="Send proof to client"
        intro={`${info.clientName} · ${info.projectName} · ${info.item}${info.proof.version > 1 ? ` · version ${info.proof.version}` : ""}`}
      />
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section aria-label="Proof">
          <ProofPreview file={info.proof.file} />
          <p className="mt-2 text-sm text-muted">
            <Link to={`/projects/${info.projectId}/pos/${info.poId}`} className="underline underline-offset-4 hover:text-ink">Open PO #{info.poNumber}</Link>
          </p>
        </section>

        <section aria-label="Email to the client" className="space-y-5 text-[15px]">
          {STATUS_NOTE[info.proof.status] && (
            <p className="rounded-md border border-rule bg-canvas px-4 py-3 text-sm">
              {STATUS_NOTE[info.proof.status]}
              {info.signature && (
                <>
                  {" "}Signed by <strong>{info.signature.name}</strong> on{" "}
                  {new Date(info.signature.signedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                  {info.signature.source === "client_portal" ? " in the client portal" : ""} · terms of {info.signature.termsVersion}.
                </>
              )}
            </p>
          )}
          {done ? (
            <div className="space-y-3 rounded-md border border-rule bg-white p-5">
              <p className="font-semibold">Sent to {done.sent} {done.sent === 1 ? "person" : "people"}.</p>
              {done.failed.length > 0 && <p className="text-risk">These didn't go through: {done.failed.join(", ")}.</p>}
              <p className="text-sm text-muted">You'll get an alert and an email when the client approves or asks for changes.</p>
            </div>
          ) : canSend ? (
            <>
              {info.orderContactsRequired && (info.contacts.length === 0 || showAdd) && (
                <div className="space-y-2 rounded-md border border-rule bg-warn-bg/60 p-4">
                  <p className="font-semibold">{info.contacts.length === 0 ? "Add contacts to this order first" : "Add someone to this order"}</p>
                  <p className="text-sm text-muted">Proofs only go to people on the order. Tick who's on it — they're saved to the order for next time.</p>
                  {(info.accountContacts ?? []).length === 0 ? (
                    <p className="text-sm text-muted">This client has no other contacts. Add one on the client's Contacts tab.</p>
                  ) : (
                    (info.accountContacts ?? []).map((c) => (
                      <label key={c.id} className="flex min-h-[36px] items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-ink"
                          checked={adding.has(c.id)}
                          onChange={() => {
                            const next = new Set(adding);
                            if (next.has(c.id)) next.delete(c.id);
                            else next.add(c.id);
                            setAdding(next);
                          }}
                        />
                        <span>{c.name}<span className="block text-[13px] text-muted">{c.email ?? "no email on file"}</span></span>
                      </label>
                    ))
                  )}
                  <div className="flex justify-end gap-2">
                    {info.contacts.length > 0 && <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>}
                    <Button size="sm" onClick={() => addToOrder.mutate()} disabled={adding.size === 0 || addToOrder.isPending}>{addToOrder.isPending ? "Adding…" : "Add to order"}</Button>
                  </div>
                </div>
              )}
              <fieldset>
                <legend className="mb-2 flex w-full items-center justify-between font-semibold">
                  Send to
                  {info.orderContactsRequired && info.contacts.length > 0 && !showAdd && (
                    <button type="button" onClick={() => setShowAdd(true)} className="text-sm font-normal text-muted underline underline-offset-4 hover:text-ink">Add someone to this order</button>
                  )}
                </legend>
                {info.contacts.length === 0 ? (
                  <p className="text-sm text-muted">{info.orderContactsRequired ? "No contacts on this order yet." : "This client has no contacts yet. Add one on the client's Contacts tab first."}</p>
                ) : (
                  <div className="space-y-1 rounded-md border border-rule bg-white p-3">
                    <label className="flex min-h-[36px] items-center gap-3 border-b border-rule pb-2 text-sm font-medium">
                      <input type="checkbox" className="h-4 w-4 accent-ink" checked={allTicked} onChange={() => setPicked(allTicked ? new Set() : new Set(withEmail.map((c) => c.id)))} />
                      Everyone
                    </label>
                    {info.contacts.map((c) => (
                      <label key={c.id} className={`flex min-h-[36px] items-center gap-3 ${c.email ? "" : "opacity-50"}`}>
                        <input type="checkbox" className="h-4 w-4 accent-ink" disabled={!c.email} checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                        <span>
                          {c.name}
                          {c.main && <span className="ml-2 text-xs text-muted">main contact</span>}
                          <span className="block text-[13px] text-muted">{c.email ?? "no email on file"}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              <label className="block">
                <span className="mb-1 flex items-center justify-between font-semibold">
                  Start from
                  <Link to="/settings/proof-templates" className="text-sm font-normal text-muted underline underline-offset-4 hover:text-ink">Manage templates</Link>
                </span>
                <select value={templateId} onChange={(e) => pickTemplate(e.target.value)} className="h-10 w-full rounded-md border border-rule bg-white px-3">
                  <option value="">A blank email</option>
                  {info.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block font-semibold">Subject</span>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} />
              </label>
              <label className="block">
                <span className="mb-1 block font-semibold">Message</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} maxLength={10000} className="w-full rounded-md border border-rule bg-white px-3 py-2" />
              </label>
              <p className="text-[13px] text-muted">
                The proof is attached and a "Review your proof" button (Approve / Request changes) is added under your message. It's sent from your email
                address, so replies come to you. These fill in for each person: {"{contact_first_name}"}, {"{client_name}"}, {"{project_name}"}, {"{item}"}, {"{rep_name}"}.
              </p>
              {error && <p role="alert" className="text-sm text-risk">{error}</p>}
              <div className="flex justify-end">
                <Button onClick={() => { setError(null); send.mutate(); }} disabled={picked.size === 0 || !subject.trim() || !body.trim() || send.isPending}>
                  {send.isPending ? "Sending…" : `Send to ${picked.size} ${picked.size === 1 ? "person" : "people"}`}
                </Button>
              </div>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
